import { Add, Close, Save, Search, Undo } from '@mui/icons-material';
import {
  ButtonGroup,
  Divider,
  Fade,
  Grid,
  MenuItem,
  Typography,
  useMediaQuery,
  Box,
  TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useState } from 'react';
// Components
import { Button, Button as SMButton, Loader, Modal, Table, SMBSelect } from '../../../components';
import { UserContext } from '../../../context';
import {
  createPlaceOrderUser,
  getDomainUsers,
  getPlaceOrderAvailableReportsTo,
  getPlaceOrderUser,
  getPlaceOrderUsers,
  updatePlaceOrderUser
} from '../../../fetch';
import { isAllowedToBeEditedOrDeleted } from '../../../helpers';
import {
  IDomainUser,
  IManagePlaceOrderReportsTo,
  IManagePlaceOrderRole,
  IManagePlaceOrderUser,
  IPlaceOrderRoleUser
} from '../../../models';
import { MobileUser } from './MobileUser';

interface IUserRoleModal {
  open: boolean;
  onClose: () => void;
  currentUser?: IManagePlaceOrderUser;
  users: IManagePlaceOrderUser[];
  isLoadingUsers: boolean;
  roles: IManagePlaceOrderRole[];
  fetchRoleUsers: (id: number) => void;
  fetchUsers: () => void;
}

export const UserRolesUserModal: FC<IUserRoleModal> = ({
  open,
  onClose,
  currentUser,
  users,
  isLoadingUsers,
  roles,
  fetchRoleUsers,
  fetchUsers
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, loadUser } = useContext(UserContext);
  const [isSearching, setSearching] = useState<boolean>(false);
  const [hasSearched, setSearched] = useState<boolean>(false);
  const [nameValue, setNameValue] = useState<string>('');
  const [workplaceValue, setWorkplaceValue] = useState<string>('');
  const [emailValue, setEmailValue] = useState<string>('');
  const [searchedUsers, setSearchedUsers] = useState<IDomainUser[] | null>(null);
  const [selectedUser, setSelectedUser] = useState<IPlaceOrderRoleUser[] | IManagePlaceOrderUser[]>(
    []
  );
  const [selectedRole, setSelectedRole] = useState<number | string>('');
  const [selectedReportsTo, setSelectedReportsTo] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const findUsers = async () => {
    try {
      setSelectedUser([]);
      setSearching(true);
      const res = await getDomainUsers({ name: nameValue, email: emailValue });
      setSearched(true);
      setSearchedUsers(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setSearching(false);
    }
  };

  const [isLoadingAvailableReportsTo, setIsLoadingAvailableReportsTo] = useState(false);
  const [availableReportsTo, setAvailableReportsTo] = useState<IManagePlaceOrderReportsTo[]>([]);
  const getAvailableReportsTo = async (accountName: string) => {
    try {
      setIsLoadingAvailableReportsTo(true);
      const res = await getPlaceOrderAvailableReportsTo(accountName);
      setAvailableReportsTo(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAvailableReportsTo(false);
    }
  };

  const [isLoadingSelectedUser, setIsLoadingSelectedUser] = useState(false);
  const getDistinctUser = async (accountName: string) => {
    try {
      setIsLoadingSelectedUser(true);
      const res = await getPlaceOrderUser(accountName);
      setSelectedWhoCanView(res?.allowedAccountNames ?? null);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingSelectedUser(false);
    }
  };

  const [isLoadingSelectedWhoCanView, setIsLoadingSelectedWhoCanView] = useState(false);
  const [whoCanView, setWhoCanView] = useState<IManagePlaceOrderUser[]>([]);
  const [selectedWhoCanView, setSelectedWhoCanView] = useState<string[]>(
    currentUser?.allowedAccountNames ?? []
  );
  const getUsersWhoCanView = async () => {
    try {
      setIsLoadingSelectedWhoCanView(true);
      const res = await getPlaceOrderUsers({ page: null });
      setWhoCanView(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingSelectedWhoCanView(false);
    }
  };

  const clearSearch = () => {
    setNameValue('');
    setEmailValue('');
    setWorkplaceValue('');
    setSearchedUsers(null);
    setSearched(false);
  };

  const handleSelect = async (user: IManagePlaceOrderUser) => {
    clearSearch();
    setSelectedUser([user]);
  };

  const handleClose = async () => {
    setSelectedUser([]);
    setSelectedReportsTo('');
    setSelectedRole('');
    setSelectedWhoCanView([]);
    clearSearch();
    onClose();
  };

  useEffect(() => {
    if (selectedRole) {
      getAvailableReportsTo(selectedUser[0]?.accountName);
      getDistinctUser(selectedUser[0]?.accountName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    if (currentUser) {
      setSelectedUser([currentUser]);
      setSelectedRole(
        roles?.filter(role => role.name === currentUser.placeOrderRole)[0].placeOrderRoleId ?? ''
      );
    }
    if (currentUser && availableReportsTo) {
      setSelectedReportsTo(
        availableReportsTo?.filter(reportsTo =>
          reportsTo?.displayName.includes(currentUser?.reportsTo)
        )[0]?.accountName ?? ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && availableReportsTo) {
      if (
        roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name ===
          'Billing Admin' ||
        roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name === 'Financial Admin'
      ) {
        setSelectedReportsTo('');
      }
      if (
        currentUser.placeOrderRole ===
        roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
      ) {
        setSelectedReportsTo(
          availableReportsTo?.filter(reportsTo =>
            reportsTo?.displayName.includes(currentUser?.reportsTo)
          )[0]?.accountName ?? ''
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableReportsTo]);

  useEffect(() => {
    setSelectedReportsTo('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    getUsersWhoCanView();
  }, []);

  const handleEdit = async () => {
    try {
      setIsSaving(true);
      const res = await updatePlaceOrderUser(currentUser.accountName, {
        displayName: currentUser.displayName,
        placeOrderRoleId: selectedRole ? Number(selectedRole) : null,
        reportsToAccountName: !isAllowedToBeEditedOrDeleted(
          roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
        )
          ? null
          : selectedReportsTo,
        allowedAccountNames: selectedWhoCanView
      });
      if (res?.Type === 'Error') {
        throw res;
      }
      fetchUsers();
      if (user.userName === selectedUser[0].accountName) loadUser(true);
      handleClose();
      enqueueSnackbar('User updated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'We were unable to update the user. Please try again.', {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    try {
      setIsSaving(true);
      const res = await createPlaceOrderUser(selectedUser[0].accountName, {
        displayName: selectedUser[0].displayName,
        placeOrderRoleId: selectedRole ? Number(selectedRole) : null,
        reportsToAccountName: !isAllowedToBeEditedOrDeleted(
          roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
        )
          ? null
          : selectedReportsTo,
        allowedAccountNames: selectedWhoCanView
      });
      if (res?.Type === 'Error') {
        throw res;
      }
      fetchUsers();
      if (user.userName === selectedUser[0].accountName) loadUser(true);
      handleClose();
      enqueueSnackbar('User created!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'We were unable to create the user. Please try again.', {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const columns = () => [
    {
      Header: 'Name',
      accessor: 'displayName',
      sort: false
    },
    {
      Header: 'Email',
      accessor: 'accountName',
      break: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IDomainUser } };
      }) => {
        return <>{original.email}</>;
      }
    },
    {
      id: 'actions',
      Header: '',
      accessor: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: any } };
      }) => {
        return (
          <>
            <SMButton
              id="find-users"
              type="button"
              color="primary"
              startIcon={<Add />}
              onClick={() => handleSelect(original)}
            >
              Add User
            </SMButton>
          </>
        );
      }
    }
  ];

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="md"
      >
        {isLoadingUsers && (
          <Loader position="centered" type="overlay">
            Loading...
          </Loader>
        )}
        <Fade in={open}>
          <div>
            <Typography variant="h5">
              {currentUser ? `Edit ${currentUser.displayName}` : 'Add a New User'}
            </Typography>
            <Divider />
            <Box mt={1}>
              {(currentUser || selectedUser?.length > 0) && (
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12}>
                    {!currentUser && selectedUser?.length > 0 && (
                      <Typography variant="h6">
                        {selectedUser[0]?.displayName
                          ? `User: ${selectedUser[0]?.displayName}`
                          : 'UserName'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={
                      isAllowedToBeEditedOrDeleted(
                        roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                      )
                        ? 6
                        : 12
                    }
                  >
                    <SMBSelect
                      variant="outlined"
                      label="Role"
                      value={selectedRole}
                      onChange={event => {
                        setSelectedRole(Number(event.target.value));
                      }}
                      required
                      handleClear={() => {
                        setSelectedRole('');
                      }}
                    >
                      {roles &&
                        roles.map((role, index) => {
                          return (
                            <MenuItem key={`${index}`} value={role.placeOrderRoleId}>
                              {role.name}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  </Grid>

                  {isAllowedToBeEditedOrDeleted(
                    roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                  ) && (
                    <Grid
                      item
                      xs={12}
                      sm={
                        isAllowedToBeEditedOrDeleted(
                          roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                        )
                          ? 6
                          : 12
                      }
                    >
                      <SMBSelect
                        variant="outlined"
                        label="Reports To"
                        onChange={event => {
                          setSelectedReportsTo(event.target.value);
                        }}
                        handleClear={() => {
                          setSelectedReportsTo(null);
                        }}
                        value={selectedReportsTo}
                        required={isAllowedToBeEditedOrDeleted(
                          roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                        )}
                        disabled={
                          isLoadingAvailableReportsTo ||
                          !isAllowedToBeEditedOrDeleted(
                            roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                          ) ||
                          !selectedRole
                        }
                      >
                        {availableReportsTo &&
                          availableReportsTo.map((reportsTo, index) => {
                            return (
                              <MenuItem key={`${index}`} value={reportsTo.accountName}>
                                {reportsTo.displayName}
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                  )}
                  {isAllowedToBeEditedOrDeleted(
                    roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                  ) && (
                    <Grid item xs={12} sm={12}>
                      <SMBSelect
                        variant="outlined"
                        label={'Other Users Who Can View My Data'}
                        value={selectedWhoCanView}
                        onChange={event => {
                          setSelectedWhoCanView(
                            typeof event.target.value === 'string'
                              ? event.target.value.split(',')
                              : event.target.value
                          );
                        }}
                        SelectProps={{
                          multiple: true
                        }}
                        handleClear={() => {
                          setSelectedWhoCanView([]);
                        }}
                        disabled={
                          isLoadingSelectedWhoCanView ||
                          isLoadingSelectedUser ||
                          !isAllowedToBeEditedOrDeleted(
                            roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                          )
                        }
                      >
                        {whoCanView &&
                          whoCanView.map((whoCanView, index) => {
                            return (
                              <MenuItem key={`${index}`} value={whoCanView.accountName}>
                                {whoCanView.displayName}
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                  )}
                </Grid>
              )}
              {!currentUser && selectedUser?.length < 1 && (
                <Box mb={1}>
                  <Typography variant="h6" gutterBottom>
                    Search for users
                  </Typography>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        autoComplete="nope"
                        label="Name"
                        name="name"
                        value={nameValue}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            findUsers();
                          }
                        }}
                        onChange={e => {
                          setNameValue(e.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        autoComplete="nope"
                        label="Email"
                        name="email"
                        value={emailValue}
                        onChange={e => setEmailValue(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <ButtonGroup>
                        <SMButton
                          id="find-users"
                          type="button"
                          variant="contained"
                          color="primary"
                          startIcon={<Search />}
                          disabled={!nameValue && !workplaceValue && !emailValue}
                          onClick={findUsers}
                        >
                          Find Users
                        </SMButton>
                        {hasSearched && (
                          <SMButton
                            id="clear-search"
                            type="button"
                            color="inherit"
                            variant="contained"
                            startIcon={<Close />}
                            onClick={async () => {
                              clearSearch();
                            }}
                          >
                            Clear Search
                          </SMButton>
                        )}
                      </ButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                      <Table
                        columns={columns()}
                        data={searchedUsers || []}
                        hidePagination
                        isLoading={isSearching}
                        mobileProps={{
                          handleAdd: (user: IManagePlaceOrderUser) => handleSelect(user)
                        }}
                        ResponsiveComponent={isMobile ? MobileUser : undefined}
                        noResultsText="No Users Found"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>

            <ButtonGroup sx={{ marginTop: '1rem' }}>
              <Button
                id="save"
                color="primary"
                variant="contained"
                disabled={
                  isSaving ||
                  // Role is Billing Admin
                  (isAllowedToBeEditedOrDeleted(
                    roles?.filter(role => role.placeOrderRoleId === selectedRole)[0]?.name
                  ) &&
                    (!selectedReportsTo || !selectedRole))
                }
                loading={isSaving ? 'Saving...' : ''}
                startIcon={<Save />}
                onClick={async () => {
                  if (currentUser) {
                    handleEdit();
                  } else {
                    handleCreate();
                  }
                }}
              >
                Save
              </Button>
              {!currentUser && selectedUser?.length > 0 && (
                <Button
                  id="save"
                  color="secondary"
                  variant="contained"
                  disabled={isSaving}
                  startIcon={<Undo />}
                  onClick={async () => {
                    setSelectedUser([]);
                    setSelectedReportsTo('');
                    setSelectedRole('');
                  }}
                >
                  Search again
                </Button>
              )}
            </ButtonGroup>
          </div>
        </Fade>
      </Modal>
    </>
  );
};
